import { useEffect, useState } from "react";
import { Autocomplete, Box, Button, Checkbox, debounce, MenuItem, Stack, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Typography } from "@mui/material";
import withReactContent from "sweetalert2-react-content";
import Swal from "sweetalert2";
import { useDispatch } from "react-redux";
import { ZoomRecordingResponse } from "../../api/models/ZoomRecordingResponse";
import { startSpinner, stopSpinner } from "../../redux/actions/spinnerActions";
import { NetDocsMatterResponse, OpenAPI, ZoomService, ZoomUserResponse } from "../../api";
import { NetDocsService } from "../../api/services/NetDocsService";
import DownloadIcon from '@mui/icons-material/Download';
import { ZoomRecordingRequest } from "../../api/models/ZoomRecordingRequest";
import { MatterData } from "../../api/models/MatterData";
import React from "react";

const MySwal = withReactContent(Swal);

export default function ZoomRecordings() {
    const [recordings, setRecordings] = useState<ZoomRecordingResponse[]>([]);
    const [users, setUsers] = useState<ZoomUserResponse[]>([]);
    
    const [matterValue, setMatterValue] = useState<NetDocsMatterResponse | null>(null);
    const [inputValue, setInputValue] = useState("");
    const [matterOptions, setMatterOptions] = useState<readonly NetDocsMatterResponse[]>([]);
    
    const [selected, setSelected] = useState<number[]>([]);

    const [filterUserName, setFilterUserName] = useState<string | undefined>();

    const dispatch = useDispatch();

    const loadRecordings = (username?: string) => {
        dispatch(startSpinner());
        const request = {
            username
        } as ZoomRecordingRequest;

        ZoomService.postApiZoomRecordings(request).then((recordings) => {
            setRecordings(recordings);
            dispatch(stopSpinner());
        }).catch(err => {
            MySwal.fire({
                icon: "error",
                title: <p className="title">Error loading data</p>,
            });
            dispatch(stopSpinner());
        });
    }

    const loadUsers = () => {
        dispatch(startSpinner());
        ZoomService.getApiZoomUsers().then((users) => {
            setUsers(users);
            dispatch(stopSpinner());
        }).catch(err => {
            MySwal.fire({
                icon: "error",
                title: <p className="title">Error loading users</p>,
            });
            dispatch(stopSpinner());
        });
    }

    const fetchMatters = React.useMemo(
        () =>
        debounce((input: string) => {
            NetDocsService.getNetDocsMatters(input).then((response) => {
              let newOptions: readonly NetDocsMatterResponse[] = [];
              if (matterValue) {
                  newOptions = [matterValue];
              }
              if (response) {
                  newOptions = [...newOptions, ...response];
              }

              setMatterOptions(newOptions);
            });
        }, 200),
        []
    );


    useEffect(() => {
        loadUsers();
        loadRecordings();
    }, []);

    useEffect(() => {
        loadRecordings(filterUserName);
    }, [filterUserName]);
    
    useEffect(() => {
    if (inputValue === "") {
        setMatterOptions(matterValue ? [matterValue] : []);
        return undefined;
    }

    fetchMatters(inputValue);

    return () => { };
    }, [matterValue, inputValue, fetchMatters]);
    
    const handleDownloadTranscript = (recording: ZoomRecordingResponse) => {
        dispatch(startSpinner());
        var request = {
            recordingId: recording.id
        } as ZoomRecordingRequest;
        
        fetch(`${OpenAPI.BASE}/api/Zoom/downloadTranscript`, {
          method: "POST",
          headers: {
            Authorization: (OpenAPI.HEADERS as any)?.Authorization,
            "Content-Type": "application/json",
          },
          body: JSON.stringify(request),
        })
          .then((response: any) => {
            response.blob().then((blob) => {
              const url = window.URL.createObjectURL(blob);
    
              const a = document.createElement("a");
              a.style.display = "none";
              a.href = url;
              a.download = `${recording.recordingDateTime}.docx`;
              document.body.appendChild(a);
              a.click();
            });
          })
          .finally(() => dispatch(stopSpinner()));
    }

    const handleSaveToNetdocs = async (recordings: number[]) => {
			dispatch(startSpinner());
			
			if (!matterValue) {
        MySwal.fire({
            icon: "warning",
            title: "Matter required",
            text: "Please select a NetDocuments matter before saving.",
        });
        dispatch(stopSpinner());
        return;
      }

			if (!recordings || recordings.length === 0) {
				MySwal.fire({
          icon: "warning",
          title: "No recordings selected",
          text: "Please select at least one recording to save.",
        });
        dispatch(stopSpinner());
        return;
			}

			const request: ZoomRecordingRequest = {
				recordingIds: recordings,
        key: matterValue?.key,
        parent: matterValue?.parent
			};
		
			try {
				const response = await fetch(
					`${OpenAPI.BASE}/api/NetDocs/saveTranscriptsToNetDocs`,
					{
						method: "POST",
						headers: {
							Authorization: (OpenAPI.HEADERS as any)?.Authorization,
							"Content-Type": "application/json",
						},
						body: JSON.stringify(request),
					}
				);
		
				if (!response.ok) {
					throw new Error("Failed to save transcript to NetDocuments");
				}
		
				await response.json();
        MySwal.fire({
          icon: "success",
          title: "Saved successfully",
          text: `${recordings.length} transcript(s) saved to NetDocuments.`,
        });

			} catch (error) {
				console.error(error);
			} finally {
				dispatch(stopSpinner());
			}
		
			dispatch(stopSpinner());
    }

    const handleSelectAllClick = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (event.target.checked) {
            const newSelected = recordings.map((n) => n.id!);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleClick = (row) => {
        const selectedIndex = selected.indexOf(row.id);
        let newSelected: number[] = [];

        if (selectedIndex === -1) {
        newSelected = selected.concat(row.id);
        } else if (selectedIndex === 0) {
        newSelected = selected.slice(1);
        } else if (selectedIndex === selected.length - 1) {
        newSelected = selected.slice(0, -1);
        } else if (selectedIndex > 0) {
        newSelected = selected
            .slice(0, selectedIndex)
            .concat(selected.slice(selectedIndex + 1));
        }

        setSelected(newSelected);
    };

    const isSelected = (id: number) => selected.indexOf(id) !== -1;
    
    return (<>
        <Box sx={{ height: 'calc(100vh - 120px)', display: 'flex', flexDirection: 'column', gap: 2, paddingTop: 2 }}>
            <div className="row">
                <div className="col-3">
                    <TextField
                        label="User"
                        select
                        value={filterUserName ?? ""}
                        onChange={(e) => setFilterUserName(e.target.value)}
                        sx={{ minWidth: 200 }}
                    >
                        <MenuItem value="">All</MenuItem>
                        {users.map(user => (
                            <MenuItem key={user.name} value={user.name ?? ''}>{user.name}</MenuItem>
                        ))}
                    </TextField>
                </div>

                <div className="col-3">
                    <Autocomplete
                        getOptionLabel={(option) =>
                            (typeof option === "string"
                            ? option
                            : option.description) as string
                        }
                        filterOptions={(x) => x}
                        options={matterOptions}
                        autoComplete
                        includeInputInList
                        filterSelectedOptions
                        value={matterValue}
                        noOptionsText="No matters"
                        onChange={(
                            event: any,
                            selectedValue: NetDocsMatterResponse | null
                        ) => {
                            setMatterOptions(
                                selectedValue
                                    ? [selectedValue, ...matterOptions]
                                    : matterOptions
                            );
                            setMatterValue(selectedValue);
                        }}
                        onInputChange={(event, newInputValue) => {
                            setInputValue(newInputValue);
                        }}
                        renderInput={(params) => (
                            <TextField {...params} label="Matter" fullWidth />
                        )}
                        />
                </div>

                <div className="col-3">
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleSaveToNetdocs(selected)}
                    >
                        Save to Netdocs
                    </Button>
                </div>

            </div>
            
            <Box 
                sx={{
                    display: 'flex',
                    overflowX: 'auto',
                    overflowY: 'hidden',
                    gap: 4,
                    flexWrap: 'nowrap',
                    flex: 1,
                    padding: 1,
                }}>
                <TableContainer
                  sx={{
                      flex: 1,
                      overflow: "auto",
                      borderRadius: 2,
                      border: "1px solid #eee",
                      backgroundColor: "#fff",
                  }}
                >
                    <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell padding="checkbox">
                                <Checkbox
                                    color="primary"
                                    indeterminate={selected.length > 0 && selected.length < recordings.length}
                                    checked={recordings.length > 0 && selected.length === recordings.length}
                                    onChange={handleSelectAllClick}
                                />
                                </TableCell>
                                <TableCell>No</TableCell>
                                <TableCell>Callee Name</TableCell>
                                <TableCell>Callee Number</TableCell>
                                <TableCell>Caller Name</TableCell>
                                <TableCell>Caller Number</TableCell>
                                <TableCell>Direction</TableCell>
                                <TableCell>Duration</TableCell>
                                <TableCell>Date Time</TableCell>
                                <TableCell>Transcript</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {recordings.map((recording, index) => {
                                const isItemSelected = isSelected(recording.id!);
                                const labelId = `enhanced-table-checkbox-${index}`;
                                
                                return (
                                    <TableRow
                                        key={index}
                                        hover
                                        onClick={() => handleClick(recording)}
                                        tabIndex={-1}
                                        selected={isItemSelected}
                                        sx={{ cursor: "pointer" }}
                                    >
                                        <TableCell padding="checkbox">
                                            <Checkbox
                                                color="primary"
                                                checked={isItemSelected}
                                                inputProps={{
                                                    "aria-labelledby": labelId,
                                                }}
                                            />
                                        </TableCell>
                                        <TableCell component="th" scope="row">
                                            {index + 1}
                                        </TableCell>
                                        <TableCell>{recording.calleeName}</TableCell>
                                        <TableCell>{recording.calleeNumber}</TableCell>
                                        <TableCell>{recording.callerName}</TableCell>
                                        <TableCell>{recording.callerNumber}</TableCell>
                                        <TableCell>{recording.direction}</TableCell>
                                        <TableCell>{recording.duration}</TableCell>
                                        <TableCell>{recording.recordingDateTime ? new Date(recording.recordingDateTime).toLocaleDateString('en-US') : "N/A"}</TableCell>
                                        <TableCell>
                                            <Button
                                                variant="contained"
                                                color="primary"
                                                startIcon={<DownloadIcon />}
                                                onClick={() => handleDownloadTranscript(recording)}
                                                >
                                            </Button>
                                        </TableCell>
                                    </TableRow>
                                )
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>
        </Box>
    </>);
}
